import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Bell, User, School } from 'lucide-react';
import NotificationCenter from '../notifications/NotificationCenter';
import SchoolSelector from './SchoolSelector';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useSchool } from '../../contexts/SchoolContext';

interface TopBarProps {
  children?: React.ReactNode;
}

export default function TopBar({ children }: TopBarProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSchoolSelector, setShowSchoolSelector] = useState(false);
  const [userName, setUserName] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef<HTMLDivElement>(null);
  const schoolSelectorRef = useRef<HTMLDivElement>(null);
  const { selectedSchool, loading: schoolLoading } = useSchool();

  useOnClickOutside(notificationRef, () => setShowNotifications(false));
  useOnClickOutside(schoolSelectorRef, () => setShowSchoolSelector(false));

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Subscribe to user profile
    const unsubscribeUser = onSnapshot(doc(db, 'users', user.uid), (doc) => {
      if (doc.exists()) {
        setUserName(doc.data().displayName || '');
      }
    });

    // Subscribe to unread notifications
    const unsubscribeNotifications = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('recipientId', '==', user.uid),
        where('read', '==', false)
      ),
      (snapshot) => {
        setUnreadCount(snapshot.size);
      }
    );

    return () => {
      unsubscribeUser();
      unsubscribeNotifications();
    };
  }, []);

  return (
    <div className="h-16 bg-surface-light border-b border-border-light px-4 lg:px-6 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {children}
        
        {/* School Selector */}
        <div ref={schoolSelectorRef} className="relative">
          <button
            data-school-selector-trigger
            onClick={() => setShowSchoolSelector(!showSchoolSelector)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background transition-colors"
          >
            <School className="w-5 h-5 text-primary-500" />
            <div className="hidden sm:block text-left">
              {schoolLoading ? (
                <span className="text-sm font-medium text-text-secondary">Loading...</span>
              ) : selectedSchool && selectedSchool.name ? (
                <>
                  <span className="text-sm font-medium text-text-primary">
                    {selectedSchool.name}
                  </span>
                  <span className="block text-xs text-text-secondary">
                    Code: {selectedSchool.code}
                  </span>
                </>
              ) : (
                <span className="text-sm font-medium text-text-secondary">
                  Select School
                </span>
              )}
            </div>
          </button>

          {showSchoolSelector && (
            <div className="absolute left-0 top-full mt-1 w-[calc(100vw-2rem)] sm:w-96 bg-surface-light 
                          rounded-lg shadow-lg border border-border-light overflow-hidden z-50">
              <SchoolSelector onClose={() => setShowSchoolSelector(false)} />
            </div>
          )}
        </div>
      </div>

      {/* Search - Hide on mobile */}
      <div className="hidden md:block flex-1 max-w-xl mx-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-light bg-background 
                     focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Notifications */}
        <div ref={notificationRef} className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-accent-500 rounded-full" />
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-1 w-[calc(100vw-2rem)] sm:w-96 bg-surface-light 
                          rounded-lg shadow-lg border border-border-light overflow-hidden z-50">
              <NotificationCenter />
            </div>
          )}
        </div>

        {/* Profile Menu */}
        <Link
          to="/profile"
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-background transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="w-5 h-5 text-primary-600" />
          </div>
          <span className="hidden sm:block text-sm font-medium text-text-primary">
            {userName || 'Profile'}
          </span>
        </Link>
      </div>
    </div>
  );
}